import type { MetricName } from "../../api/types";
import { useDeviceMetrics } from "../../hooks/queries";
import {
  formatMetricValue,
  metricLabel,
  RANGE_OPTIONS,
  seriesForMetric,
  timeFormatterForRange,
  type RangeKey,
} from "../../hooks/metrics";
import {
  CHART_COLORS,
  TimeSeriesChart,
} from "../../components/charts/TimeSeriesChart";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/Card";
import { Skeleton } from "../../components/ui/Skeleton";
import { EmptyState, ErrorState } from "../../components/ui/States";
import { Button } from "../../components/ui/Button";
import { cn } from "../../lib/utils";

function MetricChartCard({
  deviceId,
  metric,
  range,
  color,
}: {
  deviceId: string;
  metric: MetricName;
  range: RangeKey;
  color: string;
}) {
  const query = useDeviceMetrics(deviceId, metric, range);
  const points = query.data ? seriesForMetric(query.data) : [];

  return (
    <Card>
      <CardHeader>
        <CardTitle>{metricLabel(metric)}</CardTitle>
        <CardDescription>
          {metric.endsWith("_bytes") && metric !== "memory.used_bytes"
            ? "Saniyedeki değişim (bytes/s)"
            : `Aralık: ${RANGE_OPTIONS.find((option) => option.value === range)?.label ?? range}`}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {query.isPending ? (
          <Skeleton className="h-56" />
        ) : query.isError ? (
          <ErrorState
            message={`${metricLabel(metric)} serisi yüklenemedi.`}
            onRetry={() => void query.refetch()}
          />
        ) : points.length === 0 ? (
          <EmptyState title="Veri yok" description="Bu aralıkta örnek bulunamadı." />
        ) : (
          <>
            <TimeSeriesChart
              points={points}
              color={color}
              ariaLabel={`${metricLabel(metric)} zaman serisi grafiği`}
              formatValue={(value) => formatMetricValue(metric, value)}
              formatTime={timeFormatterForRange(range)}
            />
            <p className="text-muted-foreground mt-2 text-xs">
              En son değer:{" "}
              <span className="text-foreground font-medium">
                {formatMetricValue(metric, points[points.length - 1].value)}
              </span>
            </p>
          </>
        )}
      </CardContent>
    </Card>
  );
}

export function DeviceMetricsTab({
  deviceId,
  range,
  onRangeChange,
}: {
  deviceId: string;
  range: RangeKey;
  onRangeChange: (range: RangeKey) => void;
}) {
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-2">
        <span id="range-label" className="text-muted-foreground text-sm">
          Zaman aralığı:
        </span>
        <div role="group" aria-labelledby="range-label" className="flex gap-1">
          {RANGE_OPTIONS.map((option) => (
            <Button
              key={option.value}
              size="sm"
              variant={option.value === range ? "primary" : "outline"}
              aria-pressed={option.value === range}
              className={cn(option.value === range && "font-semibold")}
              onClick={() => onRangeChange(option.value)}
            >
              {option.label}
            </Button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <MetricChartCard deviceId={deviceId} metric="cpu.usage_percent" range={range} color={CHART_COLORS.cpu} />
        <MetricChartCard deviceId={deviceId} metric="memory.usage_percent" range={range} color={CHART_COLORS.memory} />
        <MetricChartCard deviceId={deviceId} metric="disk.usage_percent" range={range} color={CHART_COLORS.disk} />
        <MetricChartCard deviceId={deviceId} metric="net.rx_bytes" range={range} color={CHART_COLORS.network} />
        <MetricChartCard deviceId={deviceId} metric="net.tx_bytes" range={range} color={CHART_COLORS.other} />
        <MetricChartCard deviceId={deviceId} metric="system.process_count" range={range} color={CHART_COLORS.other} />
      </div>
    </div>
  );
}